/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { BookOpen } from 'lucide-react';

interface NotebookCoverProps {
  title: string;
  coverStyle: {
    type: 'gradient' | 'pattern' | 'solid' | 'image';
    primaryColor: string;
    secondaryColor?: string;
    patternName?: string;
    imageTheme?: 'bicycle' | 'retro' | 'nature' | 'starry';
  };
  cardCount?: number;
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const COVERS_PRESETS = [
  { type: 'image', name: 'Rustic Bicycle', primaryColor: '#f7ebd9', imageTheme: 'bicycle' },
  { type: 'image', name: 'Retro Vinyl', primaryColor: '#1e1135', imageTheme: 'retro' },
  { type: 'image', name: 'Desert Oasis', primaryColor: '#133529', imageTheme: 'nature' },
  { type: 'image', name: 'Midnight Galaxy', primaryColor: '#0a0f1d', imageTheme: 'starry' },
  { type: 'gradient', name: 'Golden Glow', primaryColor: '#f1c40f', secondaryColor: '#e67e22', patternName: 'waves' },
  { type: 'gradient', name: 'Ocean Mist', primaryColor: '#1abc9c', secondaryColor: '#3498db', patternName: 'circles' },
  { type: 'gradient', name: 'Lavender Breeze', primaryColor: '#9b59b6', secondaryColor: '#34495e', patternName: 'chevrons' },
  { type: 'gradient', name: 'Sunset Silk', primaryColor: '#e74c3c', secondaryColor: '#9b59b6', patternName: 'waves' },
  { type: 'pattern', name: 'Zen Grid', primaryColor: '#2c3e50', secondaryColor: '#34495e', patternName: 'grid' },
  { type: 'pattern', name: 'Botanical Emerald', primaryColor: '#27ae60', secondaryColor: '#2ecc71', patternName: 'dots' },
  { type: 'solid', name: 'Matte Charcoal', primaryColor: '#1e293b' },
  { type: 'solid', name: 'Blush Rose', primaryColor: '#fda4af' }
];

export const NotebookCover: React.FC<NotebookCoverProps> = ({
  title,
  coverStyle,
  cardCount = 0,
  onClick,
  className = '',
  size = 'md',
}) => {
  const { type, primaryColor, secondaryColor = '#475569', patternName = 'waves', imageTheme } = coverStyle;

  const renderImageTheme = () => {
    switch (imageTheme) {
      case 'bicycle':
        return (
          <div className="absolute inset-0 w-full h-full flex flex-col justify-between overflow-hidden">
            {/* Background color of vintage wall */}
            <div className="absolute inset-0 bg-[#f7ebd9] dark:bg-[#ecd9be]" />
            {/* Vintage Window lines & pot illustration */}
            <svg className="absolute inset-0 w-full h-full opacity-95" viewBox="0 0 176 240" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Soft shadows */}
              <rect x="25" y="15" width="45" height="60" rx="1" fill="#eaddcb" />
              <line x1="47" y1="15" x2="47" y2="75" stroke="#cbbca7" strokeWidth="1.5" />
              <line x1="25" y1="45" x2="70" y2="45" stroke="#cbbca7" strokeWidth="1.5" />
              {/* Leaning rustic bicycle outline */}
              <circle cx="55" cy="195" r="24" stroke="#4a3e2e" strokeWidth="2.5" />
              <circle cx="55" cy="195" r="21" stroke="#4a3e2e" strokeWidth="0.5" strokeDasharray="2" />
              <circle cx="130" cy="195" r="24" stroke="#4a3e2e" strokeWidth="2.5" />
              <circle cx="130" cy="195" r="21" stroke="#4a3e2e" strokeWidth="0.5" strokeDasharray="2" />
              {/* Frame segments */}
              <line x1="55" y1="195" x2="88" y2="195" stroke="#4a3e2e" strokeWidth="3" /> {/* bottom chain stay */}
              <line x1="88" y1="195" x2="75" y2="140" stroke="#4a3e2e" strokeWidth="3" /> {/* seat tube */}
              <line x1="55" y1="195" x2="75" y2="140" stroke="#4a3e2e" strokeWidth="3" /> {/* down tube */}
              <line x1="75" y1="140" x2="120" y2="135" stroke="#4a3e2e" strokeWidth="3" /> {/* top tube */}
              <line x1="88" y1="195" x2="120" y2="135" stroke="#4a3e2e" strokeWidth="3" /> {/* main stay */}
              {/* Fork & Handlebars */}
              <line x1="130" y1="195" x2="122" y2="120" stroke="#4a3e2e" strokeWidth="3" /> {/* fork/handlebar stem */}
              <path d="M 122 120 Q 115 110 110 115" stroke="#4a3e2e" strokeWidth="3" fill="none" /> {/* handlebars */}
              {/* Seat */}
              <path d="M 70 138 h 12" stroke="#4a3e2e" strokeWidth="4" strokeLinecap="round" />
              {/* Small wall foliage basket details */}
              <path d="M 25 80 L 35 110 L 45 110 L 55 80 Z" fill="#758a7d" opacity="0.85" />
              <path d="M 18 78 Q 30 55 45 70" stroke="#4d6b5e" strokeWidth="2.5" fill="none" />
              <path d="M 35 78 Q 50 60 55 75" stroke="#4d6b5e" strokeWidth="2.5" fill="none" />
            </svg>
          </div>
        );
      case 'retro':
        return (
          <div className="absolute inset-0 w-full h-full bg-[#1b0d2d] overflow-hidden flex items-center justify-center">
            {/* Laser grid effects */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-pink-500/20 to-transparent" />
            <svg className="absolute w-[120px] h-[120px] opacity-80" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="#0f051c" stroke="#e0218a" strokeWidth="2" />
              <circle cx="50" cy="50" r="35" stroke="#e0218a" strokeWidth="1" strokeDasharray="3" fill="none" />
              <circle cx="50" cy="50" r="25" stroke="#00ffff" strokeWidth="1.5" fill="none" />
              <circle cx="50" cy="50" r="10" fill="#e0218a" />
              <path d="M 10 50 A 40 40 0 0 1 90 50" stroke="rgba(255,255,255,0.15)" strokeWidth="6" fill="none" />
            </svg>
          </div>
        );
      case 'nature':
        return (
          <div className="absolute inset-0 w-full h-full bg-[#112d24] overflow-hidden">
            <div className="absolute w-20 h-20 bg-amber-400 rounded-full top-[35%] left-[20%] opacity-85" />
            <svg className="absolute bottom-0 w-full h-1/2" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M 0 100 L 0 50 Q 25 30 50 60 T 100 40 L 100 100 Z" fill="#0c1f19" />
              <path d="M 0 100 L 0 70 Q 35 50 70 80 T 100 60 L 100 100 Z" fill="#071410" opacity="0.9" />
            </svg>
          </div>
        );
      case 'starry':
        return (
          <div className="absolute inset-0 w-full h-full bg-slate-950 overflow-hidden">
            <div className="absolute top-[25%] right-[25%] w-10 h-10 bg-amber-100 rounded-full blur-xs opacity-75 shadow-[0_0_20px_#fffbeb]" />
            <div className="absolute top-[25%] right-[28%] w-10 h-10 bg-slate-950 rounded-full" />
            <svg className="absolute inset-0 w-full h-full opacity-60" viewBox="0 0 100 100">
              <circle cx="20" cy="30" r="1" fill="#ffffff" />
              <circle cx="80" cy="40" r="0.8" fill="#ffffff" />
              <circle cx="45" cy="15" r="1.2" fill="#ffffff" />
              <circle cx="70" cy="15" r="1.5" fill="#fef08a" />
              <circle cx="30" cy="80" r="1.1" fill="#ffffff" />
              <circle cx="85" cy="85" r="0.6" fill="#ffffff" />
              <path d="M 10 20 L 15 20 M 12.5 17.5 L 12.5 22.5" stroke="#ffffff" strokeWidth="0.5" />
              <path d="M 60 60 L 63 60 M 61.5 58.5 L 61.5 61.5" stroke="#fef08a" strokeWidth="0.5" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const renderPattern = () => {
    switch (patternName) {
      case 'waves':
        return (
          <svg className="absolute inset-0 w-full h-full opacity-35" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 40 C 30 20, 60 60, 90 40 C 120 20, 150 60, 180 40 L 180 120 L 0 120 Z" fill="rgba(255,255,255,0.2)" />
            <path d="M0 60 C 40 40, 80 80, 120 60 C 160 40, 200 80, 240 60 L 240 240 L 0 240 Z" fill="rgba(255,255,255,0.15)" />
            <path d="M0 100 C 50 80, 100 120, 150 100 C 200 80, 250 120, 300 100 L 300 320 L 0 320 Z" fill="rgba(255,255,255,0.1)" />
          </svg>
        );
      case 'circles':
        return (
          <svg className="absolute inset-0 w-full h-full opacity-25" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50%" cy="40%" r="50" stroke="white" strokeWidth="1" fill="none" />
            <circle cx="50%" cy="40%" r="75" stroke="white" strokeWidth="1.5" strokeDasharray="4" fill="none" />
            <circle cx="50%" cy="40%" r="100" stroke="white" strokeWidth="2" fill="none" />
            <circle cx="50%" cy="40%" r="130" stroke="white" strokeWidth="0.5" fill="none" />
          </svg>
        );
      case 'chevrons':
        return (
          <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
            <g stroke="white" strokeWidth="3" fill="none">
              <path d="M-50,0 L0,50 L50,0 M-50,40 L0,90 L50,40 M-50,80 L0,130 L50,80" />
              <path d="M100,0 L150,50 L200,0 M100,40 L150,90 L200,40 M100,80 L150,130 L200,80" />
              <path d="M-50,150 L0,200 L50,150 M-50,190 L0,240 L50,190" />
              <path d="M100,150 L150,200 L200,150 M100,190 L150,240 L200,190" strokeWidth="1.5" />
            </g>
          </svg>
        );
      case 'grid':
        return (
          <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        );
      case 'dots':
        return (
          <svg className="absolute inset-0 w-full h-full opacity-25" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots-pattern" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="12" cy="12" r="3" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots-pattern)" />
          </svg>
        );
      default:
        return null;
    }
  };

  const backgroundStyle = type === 'image'
    ? {}
    : type === 'solid'
      ? { backgroundColor: primaryColor }
      : { backgroundImage: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` };

  if (size === 'sm') {
    return (
      <div
        onClick={onClick}
        className={`relative w-8 h-11 rounded-r-[3px] shadow-xs border-y border-r border-slate-300 dark:border-slate-800 cursor-pointer overflow-hidden flex flex-col justify-between p-1 select-none ${className}`}
        style={backgroundStyle}
        id={`notebook-cover-sm-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-black/15 z-10" />
        <div className="absolute left-[1.5px] top-0 bottom-0 w-[0.5px] bg-white/20 z-10" />

        {type === 'image' && renderImageTheme()}
        {type !== 'solid' && type !== 'image' && renderPattern()}

        {type !== 'image' && (
          <div className="h-full flex items-center justify-center z-10">
            <span className="text-[6px] text-white/95 font-extrabold select-none leading-none scale-90 rotate-[-1deg]">
              {title.substring(0, 2).toUpperCase()}
            </span>
          </div>
        )}
        <div className="absolute left-2 top-0 bottom-0 w-[0.5px] bg-black/5" />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`relative w-44 h-60 rounded-r-xl shadow-lg border-y border-r border-slate-300 dark:border-slate-800 cursor-pointer overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl flex flex-col justify-between p-4 group select-none ${className}`}
      style={backgroundStyle}
      id={`notebook-cover-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-3 bg-black/15 shadow-[inset_-1px_0_2px_rgba(0,0,0,0.15)] z-10" />
      <div className="absolute left-3 top-0 bottom-0 w-1 bg-white/20 z-10" />

      {type === 'image' && renderImageTheme()}
      {type !== 'solid' && type !== 'image' && renderPattern()}

      <div className="absolute right-0 top-0 w-12 h-12 bg-white/10 rounded-bl-full pointer-events-none" />

      <div className="z-10 flex justify-end">
        <div className="bg-black/20 backdrop-blur-xs p-1.5 rounded-full text-white/90">
          <BookOpen className="w-4 h-4" />
        </div>
      </div>

      <div className="z-10 text-white mt-auto">
        <h3 className="font-sans font-bold text-lg leading-tight tracking-wide drop-shadow-md group-hover:text-amber-100 transition-colors line-clamp-3">
          {title}
        </h3>
        <p className="text-xs text-white/75 mt-1 font-mono tracking-wider">
          {cardCount} {cardCount === 1 ? 'Note' : 'Notes'}
        </p>
      </div>

      <div className="absolute left-3.5 top-0 bottom-0 w-[1px] bg-black/5" />
    </div>
  );
};
